import {
  ArrowBack,
  AttachMoneyOutlined,
  TrendingUpOutlined,
  GroupOutlined,
  PersonOutlined,
  SwapHorizOutlined,
  ApartmentOutlined,
  TuneOutlined,
  EditOutlined,
  BlockOutlined,
  CheckCircleOutlined,
  PaymentsOutlined,
} from '@mui/icons-material';
import { Button, CardContainer, MetricCard, Tag, Tabs } from 'finara-components';
import type { TabItem } from 'finara-components';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmationModal } from '@/components/common';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingState } from '@/components/common/LoadingState';
import { EditOrganizationModal } from '@/components/super-admin/clients/EditOrganizationModal';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { useUpdateOrganization } from '@/hooks/queries/useOrganizations';
import { useClientDetail } from '@/hooks/queries/useSuperAdminClients';
import type { UpdateOrganizationData } from '@/types/credits';
import type { DepositNavigationState } from '@/types/payment';

import { Navbar } from '../../../components/super-admin/general';
import { superAdminNavItems } from '../../../data/super-admin/general/navigationData';

import {
  ResumenTab,
  MovimientosTab,
  ClienteFinalTab,
  ProductosActivosTab,
  DocumentosTab,
} from './components';

export const ClientDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const [activeTab, setActiveTab] = useState('resumen');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const { data: client, isLoading, isError, refetch } = useClientDetail(clientId || '');
  const { data: accountsData } = useAccounts({
    type: 'organization',
    status: 'active',
    organization_id: clientId || '',
  });
  const updateOrganization = useUpdateOrganization();

  const orgClabe = accountsData?.data?.[0]?.clabe;

  // Format currency
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // If client not found or error, show error state
  if (isError || (!isLoading && !client)) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" userName="A Admin" />
        <div className="p-6">
          <ErrorState
            title={isError ? 'Error al cargar el cliente' : 'Cliente no encontrado'}
            message={
              isError
                ? 'No se pudo cargar la información del cliente. Por favor, intenta nuevamente.'
                : 'El cliente solicitado no existe o no está disponible.'
            }
            onRetry={isError ? () => void refetch() : undefined}
            showRetry={isError}
          />
          <div className="text-center mt-4">
            <Button variant="white" onClick={() => void navigate('/clientes')}>
              Volver al listado
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading || !client) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" userName="A Admin" />
        <div className="p-6">
          <LoadingState message="Cargando información del cliente..." />
        </div>
      </div>
    );
  }

  const handleGoBack = () => {
    void navigate('/clientes');
  };

  const isActive = client.status === 'Activo';

  const handleEditSubmit = (data: UpdateOrganizationData) => {
    if (!clientId) return;
    if (Object.keys(data).length === 0) {
      setIsEditModalOpen(false);
      return;
    }
    updateOrganization.mutate(
      { id: clientId, data },
      {
        onSuccess: () => {
          toast.success('Organización actualizada');
          setIsEditModalOpen(false);
        },
        onError: error => {
          toast.error(
            `Error al actualizar: ${error instanceof Error ? error.message : 'Error desconocido'}`
          );
        },
      }
    );
  };

  const handleStatusToggle = () => {
    if (!clientId) return;
    updateOrganization.mutate(
      { id: clientId, data: { status: isActive ? 'inactive' : 'active' } },
      {
        onSuccess: () => {
          toast.success(isActive ? 'Organización desactivada' : 'Organización activada');
          setIsStatusModalOpen(false);
        },
        onError: error => {
          toast.error(
            `Error al cambiar el estado: ${error instanceof Error ? error.message : 'Error desconocido'}`
          );
        },
      }
    );
  };

  const handleDeposit = () => {
    if (!orgClabe) return;
    const state: DepositNavigationState = {
      depositBeneficiary: { fullName: client.name, rfc: '', clabe: orgClabe },
    };
    void navigate('/pagos', { state });
  };

  // Status configuration for the tag
  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'green' | 'red' | 'gray' }> = {
      Activo: { label: 'Activo', variant: 'green' },
      Suspendido: { label: 'Suspendido', variant: 'red' },
    };

    const config = statusConfig[status] ?? {
      label: status,
      variant: 'gray' as const,
    };
    return <Tag variant={config.variant}>{config.label}</Tag>;
  };

  // Tabs configuration
  const tabs: TabItem[] = [
    {
      id: 'resumen',
      title: 'Resumen',
      onSelect: () => setActiveTab('resumen'),
    },
    {
      id: 'movimientos',
      title: 'Movimientos',
      onSelect: () => setActiveTab('movimientos'),
    },
    {
      id: 'cliente-final',
      title: 'Cliente Final',
      onSelect: () => setActiveTab('cliente-final'),
    },
    {
      id: 'productos-activos',
      title: 'Productos Activos',
      onSelect: () => setActiveTab('productos-activos'),
    },
    {
      id: 'documentos',
      title: 'Documentos',
      onSelect: () => setActiveTab('documentos'),
    },
  ];

  const renderTabContent = () => {
    if (!clientId) return null;

    switch (activeTab) {
      case 'resumen':
        return <ResumenTab clientId={clientId} />;
      case 'movimientos':
        return <MovimientosTab clientId={clientId} />;
      case 'cliente-final':
        return <ClienteFinalTab clientId={clientId} />;
      case 'productos-activos':
        return <ProductosActivosTab clientDetail={client} />;
      case 'documentos':
        return <DocumentosTab clientId={clientId} />;
      default:
        return <ResumenTab clientId={clientId} />;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" userName="A Admin" />

      {/* Header with back button */}
      <div className="mb-6">
        <Button
          className="!border-0"
          startIcon={<ArrowBack fontSize="small" />}
          variant="white"
          onClick={handleGoBack}
        >
          <span>Volver al listado</span>
        </Button>
      </div>

      {/* Client Information Card */}
      <CardContainer className="mb-6">
        <div className="md:flex md:items-start md:justify-between mb-6">
          <div className="flex items-start gap-4">
            {/* Company Icon */}
            <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center">
              <ApartmentOutlined fontSize="large" className="w-8 h-8 text-muted" />
            </div>

            <div>
              <h1 className="text-xl font-bold text-brand-white mb-2">{client.name}</h1>

              <div className="flex items-center gap-4 text-sm mb-3.5 flex-wrap">
                <div className="text-muted">
                  Código: <span className="text-brand-white">{client.code || 'N/A'}</span>
                </div>
                <div className="text-muted">
                  CLABE:{' '}
                  <span className="font-mono text-brand-white">{orgClabe || 'N/A'}</span>
                </div>
                <Tag variant="white">{client.clientType}</Tag>
                {getStatusTag(client.status)}
              </div>

              {/* Client Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-[12.25px]">
                <div>
                  <span className="text-muted">Fecha de Alta:</span>
                  <div className="text-sm font-medium text-brand-white">
                    {formatDate(client.registrationDate)}
                  </div>
                </div>
                <div>
                  <span className="text-muted">Última Actividad:</span>
                  <div className="text-sm font-medium text-brand-white">
                    {formatDate(client.lastActivity)}
                  </div>
                </div>
                <div>
                  <span className="text-muted">Empleados:</span>
                  <div className="text-sm font-medium text-brand-white">{client.employeeCount}</div>
                </div>
                <div>
                  <span className="text-muted">ID Cliente:</span>
                  <div className="text-sm font-medium text-brand-white">
                    #{client.id.slice(0, 8)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex flex-col sm:!flex-row ml-12 sm:ml-16 md:ml-0 mt-6 md:mt-0 justify-start gap-4 md:gap-3">
            <Button
              variant="white"
              startIcon={<PaymentsOutlined fontSize="small" />}
              onClick={handleDeposit}
              disabled={!orgClabe}
            >
              Depositar
            </Button>
            <Button
              variant="white"
              startIcon={<EditOutlined fontSize="small" />}
              onClick={() => setIsEditModalOpen(true)}
            >
              Editar
            </Button>
            <Button
              variant="white"
              startIcon={
                isActive ? (
                  <BlockOutlined fontSize="small" />
                ) : (
                  <CheckCircleOutlined fontSize="small" />
                )
              }
              onClick={() => setIsStatusModalOpen(true)}
            >
              {isActive ? 'Desactivar' : 'Activar'}
            </Button>
            <Button
              variant="black"
              startIcon={<TuneOutlined fontSize="small" />}
              onClick={() => void navigate(`/clientes/parametros/${clientId ?? ''}`)}
            >
              Parámetros
            </Button>
            {/* Export buttons are available in the respective tabs */}
          </div>
        </div>
      </CardContainer>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <MetricCard
          title="Volumen Total 30d"
          value={formatCurrency(client.totalVolume30d)}
          icon={<AttachMoneyOutlined fontSize="small" />}
        />

        <MetricCard
          title="Adelantos Activos"
          value={formatCurrency(client.activeAdvances)}
          icon={<TrendingUpOutlined fontSize="small" />}
        />

        <MetricCard
          title="Pagos Nómina 30d"
          value={client.payrollPayments30d.toString()}
          icon={<PersonOutlined fontSize="small" />}
        />

        <MetricCard
          title="Número de Empleados"
          value={client.employeeCount.toString()}
          icon={<GroupOutlined fontSize="small" />}
        />

        <MetricCard
          title="SPEI OUT 30d"
          value={formatCurrency(client.speiOut30d)}
          icon={<SwapHorizOutlined fontSize="small" />}
        />
      </div>

      {/* Tabs Section */}
      <div className="mb-6">
        <Tabs tabs={tabs} activeTabId={activeTab} />
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        organizationName={client.name}
        isLoading={updateOrganization.isPending}
      />

      {/* Activate / Deactivate confirmation */}
      <ConfirmationModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusToggle}
        title={isActive ? 'Desactivar organización' : 'Activar organización'}
        message={
          isActive
            ? `¿Seguro que deseas desactivar a "${client.name}"? La organización quedará suspendida.`
            : `¿Seguro que deseas activar a "${client.name}"?`
        }
        confirmText={isActive ? 'Desactivar' : 'Activar'}
        variant={isActive ? 'danger' : 'info'}
        isLoading={updateOrganization.isPending}
      />
    </div>
  );
};
